import React, { useEffect, useRef, useState } from 'react';
import lottie from 'lottie-web';
import p5 from 'p5';
import { withRouter } from 'react-router-dom';
import { FormattedMessageWithClass, Image, Link } from '../commonComponents';
import content from 'Content/content.json';
import FooterConfig, { isEmptySocial } from '../../configuration/Footer';
import { language, LANG_JP, LANG_VI, LANG_AZ } from 'Root/constants';
import { Youtube_icon, Facebook_icon, Ins_icon } from '../commonComponents/svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import './animationModule.scss';
import HomeAppPromo from "../../pages/Discover/HomeAppPromo";
import { FormattedMessage, useIntl } from 'react-intl';



const AnimationModule = () => {
  const techContainerRef = useRef(null);
  const techCardsRefs = useRef([]);
  const [techViewThreshold, setTechViewThreshold] = useState(getThreshold());
  const { formatMessage } = useIntl();

  useEffect(() => {
    const animObserverConfig = {
      root: null,
      rootMargin: '0px',
      threshold: techViewThreshold,
    };

    const animObserverCallback = (entries, observer) => {
      entries.forEach((entry) => {
        const card = entry.target;
        if (entry.isIntersecting && card.canAnimate) {
          card.classList.remove('card-hidden');
          Array.from(card.children).forEach((content) => content.classList.remove('hidden'));
          if (card.classList.contains('lacreon')) {
            card.classList.remove('minimize');
          }
          card.anim.play();
          if (card.querySelector('canvas') != null && window.innerWidth >= 1400) {
            rippleBackground.cameInView();
            rippleBackground.loop();
          }
          rippleBackground.cameInView();
          observer.unobserve(card);
        } else if (card.classList.contains('lacreon')) {
          card.classList.remove('minimize');
        }
      });
    };

    const animObserver = new IntersectionObserver(animObserverCallback, animObserverConfig);

    techCardsRefs.current.forEach((card, i) => {
      const anim = lottie.loadAnimation({
        container: card,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        path: `${process.env.PUBLIC_URL}/anims/anim_${i + 1}.json`,
      });

      card.anim = anim;
      card.canAnimate = false;

      anim.addEventListener('DOMLoaded', () => {
        const svg = card.querySelector('svg');
        if (svg) {
          card.insertBefore(svg, card.firstChild);
        }
        const scrollTop = window.scrollY;
        const scrollBottom = scrollTop + window.innerHeight;
        const cardTop = card.getBoundingClientRect().top + scrollTop;
        const cardBtmThreshold = cardTop + card.offsetHeight / 2;

        if (cardTop > scrollTop && cardBtmThreshold > scrollBottom) {
          card.classList.add('card-hidden');
          Array.from(card.children).forEach((content) => content.classList.add('hidden'));
          card.canAnimate = true;
        } else if (cardTop > scrollTop && cardBtmThreshold <= scrollBottom) {
          anim.play();
          if (card.querySelector('canvas') != null && window.innerWidth >= 1400) {
            rippleBackground.cameInView();
            rippleBackground.loop();
          }
          rippleBackground.cameInView();
        } else {
          const lastFrame = anim.totalFrames - 1;
          anim.goToAndStop(lastFrame, true);
        }
        if (card.classList.contains('lacreon')) {
          setWidthAsHeight(card, 960);
          if (window.innerWidth < 960) {
            card.classList.add('minimize');
          }
        }
        if (window.innerWidth >= 960 && window.innerWidth < 1400) {
          const tallestCardHeight = getLargestHeight(techCardsRefs.current);
          setGridOffset(tallestCardHeight);
        }
      });

      animObserver.observe(card);
    });

    window.addEventListener('resize', debounceTechResize);

    return () => {
      window.removeEventListener('resize', debounceTechResize);
    };
  }, [techViewThreshold]);

  useEffect(() => {
    rippleBackground = new p5(rippleFX, 'lacreon-card');
  }, []);

  // 工具函数
function getThreshold() {
  return window.innerWidth <= 960 ? 0.6 : 0.5;
}

function setWidthAsHeight(card, viewportMinWidth) {
  if (window.innerWidth < viewportMinWidth) {
    const height = card.offsetHeight;
    card.style.width = `${height}px`;
  } else {
    card.style.width = null;
  }
}

function getLargestHeight(cards) {
  const heights = cards.map((card) => card.offsetHeight);
  heights.sort((a, b) => b - a);
  return heights[0];
}

function setGridOffset(height) {
  const gridOffset = height / 4;
  document.querySelector('section.technology').style.setProperty('--h-gridOffset', `${gridOffset}px`);
}

const debounce = (func, delay) => {
  let debounceTimer;
  return function (...args) {
    const context = this;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(context, args), delay);
  };
};

// 为桌面设备创建 Lacreon 卡片背景效果
let rippleFX = function (p) {
  let originX, originY, endSize;

  const startSize = 420;
  const startWeight = 4;
  const moveSpeed = 1;
  const gap = 48;
  const alpha = 0.25;

  const startDelay = 3000;
  let inView, previouslyViewed, timeStamp, timer;

  const ripples = [];
  const parent = document.querySelector('#lacreon-card');

  p.setup = function () {
    p.createCanvas(parent.offsetWidth, parent.offsetHeight);
    p.noFill();
    p.colorMode(p.RGB, 255, 255, 255, 1);

    originX = (p.width - 1024) / 2 + 209;
    originY = p.height / 2 + 24;
    endSize = parent.offsetWidth * 1.25;
    inView = false;
    previouslyViewed = false;

    p.noLoop();
  };

  p.draw = function () {
    timer = p.millis();
    if (!(timer > timeStamp + startDelay && inView)) {
      return;
    }

    p.clear();

    if (ripples.length === 0) {
      addRipple();
    }

    for (let i = ripples.length - 1; i >= 0; i--) {
      let r = ripples[i];
      drawRipple(r);
      updateRipple(r, i);
      if (r === ripples[ripples.length - 1] && r.sz >= startSize + gap * 2) {
        addRipple();
      }
    }
  };

  function addRipple() {
    ripples.push({ sz: startSize, weight: startWeight });
  }

  function drawRipple(r) {
    p.stroke(255, 255, 255, alpha);
    p.strokeWeight(p.map(r.sz, startSize, endSize, startWeight, 0));
    p.ellipse(originX, originY, r.sz);
  }

  function updateRipple(r, i) {
    if (r.sz <= endSize) {
      r.sz += moveSpeed;
    } else {
      ripples.splice(i, 1);
    }
  }

  p.cameInView = function () {
    inView = true;
    timeStamp = p.millis();
  };

  p.isInView = function () {
    return inView;
  };

  p.windowResized = function () {
    p.resizeCanvas(parent.offsetWidth, parent.offsetHeight);
    originX = (parent.offsetWidth - 1024) / 2 + 209;
    originY = p.height / 2 + 24;
    endSize = parent.offsetWidth * 1.25;
  };
};

// 全局创建 rippleBackground 实例
let rippleBackground;

  const debounceTechResize = debounce(() => {
    const lacreonCard = document.querySelector('.lacreon');
    setWidthAsHeight(lacreonCard, 960);
    if (window.innerWidth >= 960 && window.innerWidth < 1400) {
      const tallestCardHeight = getLargestHeight(techCardsRefs.current);
      setGridOffset(tallestCardHeight);
    } else {
      setGridOffset(0);
    }
    if (window.innerWidth < 1400 && rippleBackground.isInView()) {
      rippleBackground.noLoop();
    } else {
      rippleBackground.loop();
    }
    setTechViewThreshold(getThreshold());
  }, 200);

  const cardsData = [
    {
      className: "innovation",
      title: formatMessage({ id: 'ani_01_title', defaultMessage: '<span>A fusion of </span><span><em>art, science </em></span><span>and </span> <span><em>innovation</em></span>' }),
      content: formatMessage({ id: 'ani_01_content', defaultMessage: 'Scientifically designed and artistically handcrafted to harmonise with your eyes. Craft your own beautiful with Define®.' }),
    },
    {
      className: "lacreon",
      id: "lacreon-card",
      title: formatMessage({ id: 'ani_02_title', defaultMessage: '<span><em>State-of-the-art </em></span><span>Lacreon® </span><span>technology</span>' }),
      content: formatMessage({ id: 'ani_02_content', defaultMessage: 'Designed to keep moisture in and irritation out for all-day comfort.' }),
    },
    {
      className: "invisible-edge",
      title: formatMessage({ id: 'ani_03_title', defaultMessage: '<span>Barely-there </span><span><em>invisible </em></span><span>edge&nbsp;design</span>' }),
      content: formatMessage({ id: 'ani_03_content', defaultMessage: "Thin, tapered lens edge design so your eyelids glide over like nothing's there."}),
    },
    {
      className: "credibility",
      title: formatMessage({ id: 'ani_04_title', defaultMessage: '<span><em>Science-backed </em></span><span><em>credibility </em></span><span>you can&nbsp;trust</span>' }),
      content: formatMessage({ id: 'ani_04_content', defaultMessage: "Enjoy quality contact lenses designed for safety and peace of mind with the world's leading contact lens brand."}),
    },
  ];

  return (
    <section ref={techContainerRef} className="technology">
      {cardsData.map((card, index) => (
        <div
          key={index}
          ref={el => techCardsRefs.current[index] = el}
          className={`card ${card.className}`}
          id={card.id || undefined}
        >
          <h2 dangerouslySetInnerHTML={{ __html: card.title }} />
          <p dangerouslySetInnerHTML={{ __html: card.content }} ></p>
        </div>
      ))}
    </section>
  );
};
export default AnimationModule;
